import { useEffect, useState } from 'react';
import { useUiStore } from '@/shared/hooks/use-shallow-store';
import {
  DESKTOP_BREAKPOINT,
  LayoutPref,
  type ResolvedLayout,
  SHORT_HEIGHT_BREAKPOINT,
  TABLET_BREAKPOINT,
} from './layout-slice';

const resolveAuto = (): ResolvedLayout => {
  if (typeof window === 'undefined' || !window.matchMedia) return 'mobile';
  // Short-height overrides width — Discord docked / PIP / mobile landscape
  // all need the mobile (scrollable) stack regardless of pixel width.
  if (window.matchMedia(SHORT_HEIGHT_BREAKPOINT).matches) return 'mobile';
  if (window.matchMedia(DESKTOP_BREAKPOINT).matches) return 'desktop';
  if (window.matchMedia(TABLET_BREAKPOINT).matches) return 'tablet';
  return 'mobile';
};

/**
 * Resolve the active layout. Combines the user preference with live
 * `matchMedia` listeners so `auto` follows window resize while explicit
 * `mobile`/`tablet`/`desktop` overrides ignore the viewport. Components
 * branch on the returned literal; CSS reads `html[data-layout]` set
 * elsewhere.
 */
export const useResolvedLayout = (): ResolvedLayout => {
  const pref = useUiStore((s) => s.layoutPref);
  const [autoLayout, setAutoLayout] = useState<ResolvedLayout>(resolveAuto);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mqDesktop = window.matchMedia(DESKTOP_BREAKPOINT);
    const mqTablet = window.matchMedia(TABLET_BREAKPOINT);
    const mqShort = window.matchMedia(SHORT_HEIGHT_BREAKPOINT);
    const onChange = (): void => setAutoLayout(resolveAuto());
    mqDesktop.addEventListener('change', onChange);
    mqTablet.addEventListener('change', onChange);
    mqShort.addEventListener('change', onChange);
    return () => {
      mqDesktop.removeEventListener('change', onChange);
      mqTablet.removeEventListener('change', onChange);
      mqShort.removeEventListener('change', onChange);
    };
  }, []);

  if (pref === LayoutPref.Mobile) return 'mobile';
  if (pref === LayoutPref.Tablet) return 'tablet';
  if (pref === LayoutPref.Desktop) return 'desktop';
  return autoLayout;
};
